/**
 * Noise this app should never spend Sentry quota on.
 *
 * `ResizeObserver loop …` is a notification the browser fires when observers
 * reschedule work across frames — benign, and emitted in bursts. Both apps used
 * to filter it by hand inside their error boundary; that filter stays there,
 * because it does a DIFFERENT job (it stops a benign notification from throwing
 * the user onto an error page). This list is about what reaches Sentry.
 */
export const IGNORED_ERRORS: RegExp[] = [
  // Anchored at the start so it matches the browser's own notification and not
  // a real failure in our code that merely mentions the observer.
  /^ResizeObserver loop /,
];

export const isIgnoredError = (message: string): boolean =>
  IGNORED_ERRORS.some((pattern) => pattern.test(message));

interface MinimalEvent {
  message?: string;
  exception?: { values?: { type?: string; value?: string }[] };
}

/**
 * Last gate before an event leaves the browser.
 *
 * `ignoreErrors` in `Sentry.init` already covers the common shape; this exists
 * because it also has to hold for events raised through `captureException`
 * directly, and because it is the hook the next filtering rule will attach to.
 */
export const buildBeforeSend =
  () =>
  <T extends MinimalEvent>(event: T): T | null => {
    // A message-only event carries no `exception` at all — reading into it
    // blindly would throw inside the error reporter itself.
    const message = event.exception?.values?.[0]?.value ?? event.message ?? '';

    return isIgnoredError(message) ? null : event;
  };
